/**
 * Geohash and distance primitives for the two-stage proximity search
 * (docs/03-domain-model.md 3.4).
 *
 * D1 (SQLite) has no spatial index, so "places within r metres of here" is
 * answered in two steps:
 *
 *   1. a coarse, index-friendly cut in SQL — the geohash cell the anchor falls
 *      into plus its eight neighbours, expressed as prefix RANGES so that the
 *      `(org_id, geohash)` index can be used;
 *   2. an exact Haversine pass in the Worker, which filters by radius and
 *      sorts by distance.
 *
 * Everything here is pure: no I/O, no clock, no randomness. That is deliberate
 * — this is the one part of the P0 prototype that is meant to outlive it, so it
 * has to be testable without a database.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** Geohash alphabet (base32, "ailo" removed to avoid look-alike characters). */
const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

/**
 * Precision stored in `places.geohash`.
 *
 * Seven characters is ~153m x ~153m at the equator. Shorter precisions are
 * obtained by prefix, so storing 7 also gives us 6, 5, 4 … for free — which is
 * why the column holds one value rather than one column per precision.
 */
export const STORED_GEOHASH_PRECISION = 7;

/**
 * Mean Earth radius (IUGG), metres.
 *
 * Exported because the same constant has to appear inside the SQL variant of
 * the distance calculation; two copies would drift and the two code paths would
 * disagree by a few metres at the radius boundary.
 */
export const EARTH_RADIUS_M = 6371008.8;

/** Metres walked per minute, the conversion docs/03-domain-model.md 3.4 fixes. */
export const WALKING_METERS_PER_MINUTE = 80;

/** Metres per degree of latitude. Constant enough for a coarse cell sizing. */
const METERS_PER_DEGREE_LAT = 111320;

const toRadians = (deg: number): number => (deg * Math.PI) / 180;

function assertFinite(name: string, value: number): void {
  if (!Number.isFinite(value)) {
    throw new TypeError(`${name} must be a finite number, received ${String(value)}`);
  }
}

/**
 * Encode a coordinate as a geohash of `precision` characters.
 *
 * Latitude is clamped and longitude wrapped, so a caller that hands in
 * `lng = 180` (a legal coordinate) gets the same cell as `lng = -180` instead
 * of an out-of-range hash.
 */
export function encodeGeohash(
  lat: number,
  lng: number,
  precision: number = STORED_GEOHASH_PRECISION,
): string {
  assertFinite('lat', lat);
  assertFinite('lng', lng);
  if (!Number.isInteger(precision) || precision < 1 || precision > 12) {
    throw new RangeError(`precision must be an integer in 1..12, received ${String(precision)}`);
  }

  let latMin = -90;
  let latMax = 90;
  let lngMin = -180;
  let lngMax = 180;

  const clampedLat = Math.min(90, Math.max(-90, lat));
  const wrappedLng = wrapLongitude(lng);

  let hash = '';
  let chunk = 0;
  let bitsInChunk = 0;
  let splittingLongitude = true;

  while (hash.length < precision) {
    if (splittingLongitude) {
      const mid = (lngMin + lngMax) / 2;
      if (wrappedLng >= mid) {
        chunk = (chunk << 1) | 1;
        lngMin = mid;
      } else {
        chunk = chunk << 1;
        lngMax = mid;
      }
    } else {
      const mid = (latMin + latMax) / 2;
      if (clampedLat >= mid) {
        chunk = (chunk << 1) | 1;
        latMin = mid;
      } else {
        chunk = chunk << 1;
        latMax = mid;
      }
    }
    splittingLongitude = !splittingLongitude;

    bitsInChunk += 1;
    if (bitsInChunk === 5) {
      hash += BASE32[chunk];
      chunk = 0;
      bitsInChunk = 0;
    }
  }

  return hash;
}

export interface GeohashCell {
  hash: string;
  /** Centre of the cell. */
  center: LatLng;
  /** Full height of the cell in degrees of latitude. */
  latSpan: number;
  /** Full width of the cell in degrees of longitude. */
  lngSpan: number;
}

/** Decode a geohash back to its cell (centre + extent). */
export function decodeGeohash(hash: string): GeohashCell {
  if (hash.length === 0) {
    throw new RangeError('geohash must not be empty');
  }

  let latMin = -90;
  let latMax = 90;
  let lngMin = -180;
  let lngMax = 180;
  let splittingLongitude = true;

  for (const character of hash) {
    const value = BASE32.indexOf(character);
    if (value < 0) {
      throw new RangeError(`"${character}" is not a geohash character`);
    }
    for (let bit = 4; bit >= 0; bit -= 1) {
      const isOne = ((value >> bit) & 1) === 1;
      if (splittingLongitude) {
        const mid = (lngMin + lngMax) / 2;
        if (isOne) lngMin = mid;
        else lngMax = mid;
      } else {
        const mid = (latMin + latMax) / 2;
        if (isOne) latMin = mid;
        else latMax = mid;
      }
      splittingLongitude = !splittingLongitude;
    }
  }

  return {
    hash,
    center: { lat: (latMin + latMax) / 2, lng: (lngMin + lngMax) / 2 },
    latSpan: latMax - latMin,
    lngSpan: lngMax - lngMin,
  };
}

/** Width and height, in metres, of a cell at `precision` at a given latitude. */
export function geohashCellSizeMeters(
  precision: number,
  lat: number,
): { widthMeters: number; heightMeters: number } {
  const bits = precision * 5;
  const latBits = Math.floor(bits / 2);
  const lngBits = bits - latBits;
  const latSpan = 180 / 2 ** latBits;
  const lngSpan = 360 / 2 ** lngBits;
  return {
    heightMeters: latSpan * METERS_PER_DEGREE_LAT,
    widthMeters: lngSpan * METERS_PER_DEGREE_LAT * Math.cos(toRadians(lat)),
  };
}

/**
 * Finest precision whose 3x3 block of cells is guaranteed to contain every
 * point within `radiusMeters` of an anchor at `lat`.
 *
 * The anchor can sit anywhere inside its own cell, including hard against an
 * edge, so the block only covers the circle when a single cell is at least as
 * large as the radius in both directions. Cells get narrower with latitude, so
 * the choice depends on where you are: the same 800m search picks a coarser
 * precision in Sapporo than in Naha.
 *
 * Matches the table in docs/03-domain-model.md 3.4 at Japanese latitudes
 * (<=500m -> 6, <=2km -> 5) without hard-coding those two rows.
 */
export function chooseGeohashPrecision(
  lat: number,
  radiusMeters: number,
  maxPrecision: number = STORED_GEOHASH_PRECISION,
): number {
  assertFinite('radiusMeters', radiusMeters);
  if (radiusMeters <= 0) {
    throw new RangeError(`radiusMeters must be positive, received ${String(radiusMeters)}`);
  }

  for (let precision = maxPrecision; precision >= 1; precision -= 1) {
    const { widthMeters, heightMeters } = geohashCellSizeMeters(precision, lat);
    if (Math.min(widthMeters, heightMeters) >= radiusMeters) {
      return precision;
    }
  }
  return 1;
}

/** Longitude normalised into [-180, 180). */
function wrapLongitude(lng: number): number {
  const wrapped = ((((lng + 180) % 360) + 360) % 360) - 180;
  return wrapped;
}

/**
 * The anchor's cell plus its eight neighbours, at the precision
 * `chooseGeohashPrecision` picks for this radius.
 *
 * Returned sorted and de-duplicated: near a pole several offsets collapse onto
 * the same cell, and duplicate prefixes would mean the same rows come back
 * twice from the OR-of-ranges query.
 */
export function geohashCellsCovering(
  anchor: LatLng,
  radiusMeters: number,
  maxPrecision: number = STORED_GEOHASH_PRECISION,
): { precision: number; cells: string[] } {
  const precision = chooseGeohashPrecision(anchor.lat, radiusMeters, maxPrecision);
  const centerCell = decodeGeohash(encodeGeohash(anchor.lat, anchor.lng, precision));

  const cells = new Set<string>();
  for (const latStep of [-1, 0, 1]) {
    for (const lngStep of [-1, 0, 1]) {
      const lat = centerCell.center.lat + latStep * centerCell.latSpan;
      if (lat < -90 || lat > 90) {
        // Past a pole there is no neighbouring cell; the centre row already
        // covers everything reachable in that direction.
        continue;
      }
      const lng = centerCell.center.lng + lngStep * centerCell.lngSpan;
      cells.add(encodeGeohash(lat, lng, precision));
    }
  }

  return { precision, cells: [...cells].sort() };
}

/**
 * Half-open range `[start, endExclusive)` matching every geohash that begins
 * with `prefix`.
 *
 * Used instead of `LIKE 'prefix%'` on purpose. SQLite's LIKE is
 * case-insensitive for ASCII by default, which disqualifies it from using the
 * `(org_id, geohash)` index; a pair of comparisons is always index-friendly.
 *
 * `'{'` is the byte after `'z'`, the highest character in the geohash
 * alphabet, so no valid geohash can sort at or above `prefix + '{'` while
 * still starting with `prefix`.
 */
export function geohashPrefixRange(prefix: string): { start: string; endExclusive: string } {
  if (prefix.length === 0) {
    throw new RangeError('prefix must not be empty');
  }
  return { start: prefix, endExclusive: `${prefix}{` };
}

/** Great-circle distance in metres. */
export function haversineMeters(from: LatLng, to: LatLng): number {
  const fromLat = toRadians(from.lat);
  const toLat = toRadians(to.lat);
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);

  const a =
    Math.sin(deltaLat / 2) ** 2 + Math.cos(fromLat) * Math.cos(toLat) * Math.sin(deltaLng / 2) ** 2;

  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Straight-line walking time in minutes at 80 m/min.
 *
 * This is a straight line, not a route, so it always under-estimates. The UI is
 * required to say so ("直線約 400m") rather than present it as a walking
 * distance (docs/03-domain-model.md 3.4).
 */
export function walkingMinutes(meters: number): number {
  return meters / WALKING_METERS_PER_MINUTE;
}

export interface RankedByDistance<T> {
  item: T;
  distanceMeters: number;
}

/**
 * Exact second stage of the proximity search: drop everything outside the
 * radius, sort by distance, cut to `limit`.
 *
 * Structural typing does the work here — a `places` row already has `lat` and
 * `lng`, so it can be ranked without being mapped into a separate shape first.
 */
export function rankByDistance<T extends LatLng>(
  anchor: LatLng,
  items: readonly T[],
  options: { radiusMeters: number; limit?: number },
): RankedByDistance<T>[] {
  const within: RankedByDistance<T>[] = [];

  for (const item of items) {
    const distanceMeters = haversineMeters(anchor, item);
    if (distanceMeters <= options.radiusMeters) {
      within.push({ item, distanceMeters });
    }
  }

  within.sort((a, b) => a.distanceMeters - b.distanceMeters);
  return options.limit === undefined ? within : within.slice(0, options.limit);
}
